import { useState } from 'react'
import type { WorkCategoryInfo, WorkSubcategoryInfo } from '../../types/work'
import WorkSubcategoryCard from './WorkSubcategoryCard'

interface WorkCategoryDetailProps {
  category: WorkCategoryInfo
  onSubcategorySelect: (sub: WorkSubcategoryInfo) => void
  selectedSubcategory?: string
}

export default function WorkCategoryDetail({
  category,
  onSubcategorySelect,
  selectedSubcategory,
}: WorkCategoryDetailProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  function handleSelect(sub: WorkSubcategoryInfo) {
    setExpandedId(sub.id === expandedId ? null : sub.id)
    onSubcategorySelect(sub)
  }

  const expanded = category.subcategories.find(s => s.id === expandedId)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Category header */}
      <div>
        <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
          {category.label}
        </div>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {category.description}
        </div>
      </div>

      {/* Subcategory grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '10px',
        }}
      >
        {category.subcategories.map(sub => (
          <WorkSubcategoryCard
            key={sub.id}
            subcategory={sub}
            selected={selectedSubcategory === sub.id}
            onSelect={() => handleSelect(sub)}
          />
        ))}
      </div>

      {/* Expanded detail panel */}
      {expanded && (
        <div
          style={{
            padding: '16px',
            borderRadius: '12px',
            background: 'rgba(99, 102, 241, 0.05)',
            border: '1px solid rgba(99, 102, 241, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
            {expanded.label} — Details
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted, #6b7280)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Example Input
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', lineHeight: 1.5 }}>
                {expanded.example_input}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted, #6b7280)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Example Output
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', lineHeight: 1.5 }}>
                {expanded.example_output}
              </div>
            </div>
          </div>

          {expanded.accepted_file_types && expanded.accepted_file_types.length > 0 && (
            <div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted, #6b7280)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Supported Formats
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {expanded.accepted_file_types.map(fmt => (
                  <span
                    key={fmt}
                    style={{
                      padding: '2px 8px',
                      borderRadius: '20px',
                      background: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                      fontSize: '11px',
                      color: 'var(--text-secondary)',
                      fontFamily: 'monospace',
                    }}
                  >
                    {fmt}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
